import { useMemo } from 'react';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import StatTile from '../charts/StatTile';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { toLocalIsoDate } from '../../../utils/dateUtils';
import { isLate, shippingThisWeek } from './threadData';
import { useProductionOutputs } from './useThreadData';

interface Props {
    institutionId: string;
    rows: IOrderDashboardRow[] | null;
    loading: boolean;
    /** True when the report cut the list at its row limit: the counts are then a floor, not the full number. */
    truncated?: boolean;
}

/**
 * Merchandising figures for the Thread board, all from data the merchandiser can already read:
 * the order dashboard rows (open, late, shipping this week) and, with costing access, today's output.
 * Nothing is compared with a made-up baseline.
 */
export default function MerchStatRow({ institutionId, rows, loading, truncated = false }: Props) {
    const { hasPermission } = useAuth();
    const today = useMemo(() => toLocalIsoDate(new Date()), []);
    const canOutput = hasPermission(PERMISSIONS.COSTING_VIEW);
    const outputs = useProductionOutputs(institutionId, canOutput ? { from: today, to: today } : null);

    if (loading && rows === null) {
        return (
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} aria-label="Loading figures" /></Box>
            </Paper>
        );
    }
    if (rows === null) return null;

    const late = rows.filter(isLate).length;
    const shipping = shippingThisWeek(rows, today).length;
    const finished = (outputs ?? []).filter(o => o.stage === 'FINISHED').reduce((sum, o) => sum + o.quantity, 0);
    const hasOutputToday = (outputs ?? []).length > 0;

    return (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, minWidth: 0 }} data-testid="thread-stat-row" data-help-section="dashboard.thread.stats">
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                <StatTile label="Open orders" value={truncated ? `${rows.length}+` : rows.length} sub={truncated ? 'List cut at the report limit' : undefined} />
                <StatTile label="Late orders" value={late} tone={late > 0 ? 'critical' : 'good'} sub={late > 0 ? 'At least one T&A step overdue' : 'None overdue'} />
                <StatTile label="Shipping this week" value={shipping} sub="Next 7 days" tone={shipping > 0 ? 'warning' : 'default'} />
                {canOutput && outputs !== null && (
                    <StatTile label="Finished pcs today" value={hasOutputToday ? finished.toLocaleString('en-US') : 'Not started'} sub="From daily output entries" />
                )}
            </Stack>
        </Paper>
    );
}
